import { getApiUrl } from "./consts";

/**
 * Converts an array to a comma-separated parameter string
 * @param {string[]} array
 * @returns Parameter string
 */
const arrayToParameter = (array) => array.reduce((acc, cur) => acc + "," + cur);

/**
 * Fetches the channel data rows from the API
 * @param {string} accessToken Access token from Auth0
 * @param {string[]} [fields] Fields to include in response
 * @returns Channel data rows
 */
export async function fetchChannelDataRows(accessToken, fields) {
    // No cached logic
    const apiUrl = getApiUrl();
    let url = `${apiUrl}/channel`;
    if (fields && fields.length > 0) url += `?fields=${["channel_id", ...fields].join(",")}`;

    const res = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    const data = await res.json();
    if (res.status !== 200) {
        throw new Error(`${res.status}: ${data.message}`)
    }
    return data;
}

/**
 * Clears the cached rows from session storage
 */
export function clearRowCache() {
    sessionStorage.removeItem("rows");
}

/**
 * @typedef CachedRows
 * @type {object}
 * @property {object[]} rows
 * @property {string[]} fetchedFields
 */
/**
 * Gets the channel data rows from session storage
 * @returns {CachedRows} Channel data rows that have been cached, or null
 */
// function getCachedRows() {
//     return JSON.parse(sessionStorage.getItem("rows")) || { rows: [], fetchedFields: [] };
// }


/**
 * Sets the channel data row cache
 * @param {object[]} rows Channel data rows
 * @param {string[]} fetchedFields Fields that have been fetched
 */
// function setCachedRows(rows, fetchedFields) {
//     sessionStorage.setItem("rows", JSON.stringify({ rows, fetchedFields }));
// }


/**
 * Fetches the channel data for a specific channel from the API
 * @param {number} channelID The row ID for the channel
 * @param {string} accessToken Access token from Auth0
 * @param {string[]} [fields] Fields to include in response
 * @returns Channel data
 */
export async function fetchChannelData(accessToken, channelID, fields) {
    const apiUrl = getApiUrl();
    let url = `${apiUrl}/channel/${channelID}`;
    if (fields) url += `?fields=${arrayToParameter(fields)}`;
    const res = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    const data = await res.json();
    if (res.status !== 200) {
        throw new Error(`${res.status}: ${data.message}`)
    }
    return data;
}


export async function fetchSchema(accessToken){

    let cachedSchema = getCachedSchema()
    if (cachedSchema) return cachedSchema

    const apiUrl = getApiUrl();
    let url = `${apiUrl}/schema/channel`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    })

    const schema = await res.json();
    if (res.status !== 200) {
        throw new Error(`${res.status}: ${schema.message}`)
    }
    //TODO: Set timeouts for cache here and other places to not pull incorrect data on schema change
    setCachedSchema(schema)
    return schema;
}


function getCachedSchema() {
    return JSON.parse(sessionStorage.getItem("schema"));
}


function setCachedSchema(schema) {
    sessionStorage.setItem("schema", JSON.stringify(schema));
}

export async function getLiveStatus(accessToken, channelID){

    const apiUrl = getApiUrl();
    let url = `${apiUrl}/channel/${channelID}/status`;
    const res = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    const data = await res.json();
    if (res.status !== 200) {
        throw new Error(`${res.status}: ${data.message}`)
    }
    return data;
}

export function getNextLive(){
    return "5pm PST";
}

export async function uploadLogo(accessToken, channelID, logo){
    
    const apiUrl = getApiUrl();

    // get presigned url to upload to
    const urlRes = await fetch(`${apiUrl}/logo/${channelID}`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    if (!urlRes.ok) {
        console.error("Error fetching logo upload URL");
        return;
    }
    const { url } = await urlRes.json();

    // upload logo to bucket
    const uploadRes = await fetch(url, {
        method: "PUT",
        headers: {
            "Content-Type": "image/svg+xml",
        },
        body: logo,
    });
    if (!uploadRes.ok) {
        console.log(uploadRes)
        console.error("Error uploading logo");
        return;
    }
}


export async function fetchChannelSchedule(accessToken, channelID){

    const apiUrl = getApiUrl();
    let todaysDate = new Date().toISOString().split("T")[0]

    const res = await fetch(
        `${apiUrl}/channel/${channelID}/schedule?startDateTime=${todaysDate}T00:00Z&daysRequested=3`
        ,{
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );

    const data = await res.json();
    
    if (res.status !== 200) {
        throw new Error(`${res.status}: ${data.message}`)
    }

    const programs = data.map((program) => ({
        title: program.title,
        start: new Date(program.start),
        stop: new Date(program.stop),
    }));

    return programs
}


export async function submitChannelData(accessToken, channelID, stationAttributes, stateObject, contactsList){

    let numericFields = ['dma_code', 'bsid3_or_tsid1_verance_required_info', 'major_minor_virtual_channel_number', 'verance_service_id']

    let updates = {}

    for(let i = 0; i < stationAttributes.length; i++){
        let stationAttribute = stationAttributes[i]
        let entry = stateObject[stationAttribute]

        if (entry.updatedState && entry.validState){
            if (numericFields.includes(stationAttribute)) updates[stationAttribute] = Number(entry.currentValue)
            if (entry.currentValue === "") entry.currentValue = null
            updates[stationAttribute] = entry.currentValue
        }
    }

    updates.station_contacts = contactsList

    updates = JSON.stringify(updates)
    const apiUrl = getApiUrl();
    let url = `${apiUrl}/channel/${channelID}`;
    const res = await fetch(url, {
        method: 'PATCH',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: updates,
    })
    
    if (!res.ok) {
        const errorText = await res.text();
        console.error('PATCH failed:', res.status, errorText);
        throw new Error(`Failed to save changes: ${res.status}`);
    }
    
    sessionStorage.removeItem('rows')
}
/**
 * Adds a new channel row to the database
 * @param {string} accessToken Auth0 access token
 * @param {string} callSign
 * @param {string} affiliate
 * @returns {Promise<object>}
 */
  export async function addChannelRow(accessToken, callSign, affiliate) {
      const apiUrl = getApiUrl();
      const channel_id = callSign.toUpperCase() + "-" + affiliate.toUpperCase();
      const url = `${apiUrl}/channel/${channel_id}`;  // Add channel_id to URL
      const body = JSON.stringify({
          call_sign: callSign,
          affiliation: affiliate
      });
        // In apiRequests.js around line 254
        console.log('About to send request with token:', accessToken ? 'Token exists' : 'No token');
        console.log('Request URL:', url);
        console.log('Request headers:', {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        });

      const res = await fetch(url, {
          method: "POST",
          headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
          },
          body,
      });
        const responseText = await res.text();
  console.log('Response status:', res.status);
  console.log('Response body:', responseText);

  if (!res.ok) {
      throw new Error(`Failed to add channel row: ${res.status} - ${responseText}`);
  }
}

/**
 * Fetches the changelog data for a specific channel
 * @param {string} accessToken Access token from Auth0
 * @param {string} channelID Channel ID to get changelog for
 * @param {number} [limit] Optional limit on number of records
 * @returns Channel changelog data
 */
export async function fetchChannelChangelog(accessToken, channelID) {
    const apiUrl = getApiUrl();
    let url = `${apiUrl}/channel/${channelID}/changelog`;
    // Don't limit when fetching all entries for client-side filtering
    // if (limit) url += `?limit=${limit}`;

    const res = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    const data = await res.json();
    if (res.status !== 200) {
        throw new Error(`${res.status}: ${data.message}`)
    }
    
    console.log("Changelog data for channel", channelID, ":", data);
    return data;
}
